type AnyObject = { [key: string]: any };

/**
 * @param condition
 * @param element
 * @returns value if condition is true, otherwise an empty object
 */
export const insertObjectIf = (condition: any, element: object) =>
  condition || condition?.length > 0 ? element : {};

export default insertObjectIf;

/**
 * @param obj - Object to convert to snake case
 */
export const toSnakeCase = (obj: AnyObject) => {
  if (typeof obj !== "object" || obj === null) return obj;
  if (typeof obj !== "object" || obj === null) return obj;

  return Object.keys(obj).reduce((acc: AnyObject, key: string) => {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj[key]);
    return acc;
  }, {});
};

/**
 * @param obj - Object to convert to camel case
 */
export const toCamelCase = (obj: AnyObject): AnyObject => {
  if (typeof obj !== "object" || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  return Object.keys(obj).reduce((acc: AnyObject, key: string) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

/**
 * Parse number from formatted string (removes commas and other formatting)
 * @param value - Formatted string to parse (e.g., "1,800" or "1.234,56")
 * @returns Parsed number or 0 if invalid
 * @example
 * parseNumber("1,800") // returns 1800
 * parseNumber("275") // returns 275
 * parseNumber("invalid") // returns 0
 */
export const parseNumber = (value: string | number): number => {
  if (typeof value === "number") return value;
  return Number(value.replace(/,/g, "")) || 0;
};

/**
 * Format number to Japanese locale string (with comma separators)
 * @param value - Number to format
 * @returns Formatted string or empty string if no value
 * @example
 * formatNumber(1800) // returns "1,800"
 * formatNumber(275) // returns "275"
 * formatNumber(undefined) // returns ""
 */
export const formatNumber = (value?: number): string => 
  value !== undefined && value !== null ? value.toLocaleString("ja-JP") : "";
